<template>
  <el-popover
    :key="popoverKey"
    v-model="panelVisible"
    :placement="isMobile ? 'bottom-start' : 'right-start'"
    trigger="manual"
    :width="360"
    popper-class="menu-search-popper"
  >
    <div class="menu-search-panel" :role="normalizedQuery ? 'listbox' : 'region'" aria-label="菜单搜索结果">
      <p v-if="status === 'loading'" class="menu-search-empty">正在加载菜单…</p>
      <p v-else-if="status === 'error'" class="menu-search-empty">菜单加载失败，请刷新页面重试</p>
      <template v-else-if="!normalizedQuery">
        <div class="menu-search-heading">
          <span>历史搜索</span>
          <button v-if="history.length" type="button" class="menu-search-action" aria-label="清空历史搜索" title="清空历史搜索" @click="removeAllHistory"><i class="el-icon-delete" aria-hidden="true" /></button>
        </div>
        <p v-if="!history.length" class="menu-search-empty">输入菜单名称或拼音，快速找到页面</p>
        <div v-if="history.length" class="menu-search-history-tags">
          <div v-for="item in history" :key="item.query" class="menu-search-history">
            <button type="button" class="menu-search-history-query" :title="item.query" @click="useHistory(item.query)">{{ item.query }}</button>
            <button type="button" class="menu-search-delete" :aria-label="'删除搜索记录 ' + item.query" @click="removeHistory(item.query)">×</button>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="menu-search-heading">搜索结果</div>
        <p v-if="!results.length && pinyinLoading" class="menu-search-empty">正在加载拼音搜索…</p>
        <p v-else-if="!results.length" class="menu-search-empty">未找到菜单，请试试其他关键词</p>
        <button
          v-for="(item, index) in results"
          :key="item.path"
          type="button"
          role="option"
          class="menu-search-result"
          :class="{'is-selected': index === selectedIndex}"
          :aria-selected="index === selectedIndex"
          @mouseenter="selectedIndex = index"
          @click="selectResult(item)"
        >
          <span class="menu-search-title">{{ item.title }}</span>
          <span v-if="item.parents.length" class="menu-search-parents">{{ item.parents.join(' / ') }}</span>
        </button>
        <p v-if="overflow" class="menu-search-more">结果较多，请输入更具体的关键词</p>
      </template>
    </div>
    <div slot="reference" ref="searchRoot" class="menu-search-reference">
      <button
        v-if="collapsed"
        type="button"
        class="menu-search-collapsed"
        aria-label="展开菜单搜索"
        @click="activate"
      ><i class="el-icon-search" /></button>
      <el-input
        v-else
        ref="searchInput"
        v-model="query"
        size="small"
        clearable
        prefix-icon="el-icon-search"
        placeholder="搜索菜单"
        label="搜索菜单"
        @focus="activate"
        @click.native="activate"
        @input="scheduleSearch"
        @clear="refreshResults"
        @keydown.native="onKeydown"
        @compositionstart.native="composing = true"
        @compositionend.native="onCompositionEnd"
      />
    </div>
  </el-popover>
</template>

<script>
import { isExternal } from '@/utils/validate'
import { buildMenuIndex, addPinyin, searchMenuIndex } from './menuSearchIndex'
import { EPHEMERAL_HISTORY_KEY, historyKey, normalizeQuery, readHistory, rememberQuery, deleteQuery, clearHistory } from '@/utils/menuSearchHistory'

export default {
  name: 'MenuSearch',
  props: {
    routes: {
      type: Array,
      required: true
    },
    status: {
      type: String,
      required: true
    },
    collapsed: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      query: '',
      panelVisible: false,
      results: [],
      overflow: false,
      history: [],
      selectedIndex: 0,
      composing: false,
      pinyinLoading: false
    }
  },
  computed: {
    isMobile() {
      return this.$store.state.app.device === 'mobile'
    },
    popoverKey() {
      return `${this.isMobile ? 'mobile' : 'desktop'}-${this.collapsed ? 'collapsed' : 'expanded'}`
    },
    normalizedQuery() {
      return normalizeQuery(this.query)
    }
  },
  watch: {
    isMobile() {
      // 视口跨断点时，旧 Popper 可能保留桌面坐标；重开时重新计算位置。
      this.panelVisible = false
    },
    collapsed(value) {
      if (value) this.panelVisible = false
    },
    routes: {
      immediate: true,
      handler(routes) {
        this.indexEntries = buildMenuIndex(routes)
        if (this.pinyinFunction) addPinyin(this.indexEntries, this.pinyinFunction)
        this.refreshResults()
      }
    },
    status() {
      this.refreshResults()
    },
    panelVisible(value) {
      if (value) this.history = readHistory(this.currentHistoryKey())
    }
  },
  mounted() {
    document.addEventListener('mousedown', this.closeOnOutsideClick)
  },
  beforeDestroy() {
    this.destroyed = true
    clearTimeout(this.searchTimer)
    document.removeEventListener('mousedown', this.closeOnOutsideClick)
  },
  methods: {
    currentHistoryKey() {
      const keycloak = this.$keycloak
      const token = keycloak && (keycloak.idTokenParsed || keycloak.tokenParsed ||
        (keycloak.keycloak && keycloak.keycloak.tokenParsed))
      return historyKey({
        authUrl: process.env.VUE_APP_KEYCLOAK_OPTIONS_URL,
        realm: process.env.VUE_APP_KEYCLOAK_OPTIONS_REALM,
        sub: token && token.sub
      }) || EPHEMERAL_HISTORY_KEY
    },
    activate() {
      if (this.collapsed) {
        this.$store.dispatch('app/toggleSideBar').then(() => {
          this.$nextTick(() => this.activate())
        })
        return
      }
      this.panelVisible = true
      this.$nextTick(() => {
        if (this.$refs.searchInput) this.$refs.searchInput.focus()
      })
      this.ensurePinyin()
    },
    ensurePinyin() {
      if (this.pinyinFunction || this.pinyinPromise) return
      this.pinyinLoading = true
      this.pinyinPromise = import(/* webpackChunkName: "menu-search-pinyin" */ 'pinyin-pro')
        .then(module => {
          if (this.destroyed) return
          this.pinyinFunction = module.pinyin
          addPinyin(this.indexEntries, this.pinyinFunction)
          this.pinyinLoading = false
          this.refreshResults()
        })
        .catch(() => {
          if (!this.destroyed) {
            this.pinyinLoading = false
            this.pinyinPromise = null
            this.refreshResults()
          }
        })
    },
    scheduleSearch() {
      if (this.composing) return
      if (!this.panelVisible) this.panelVisible = true
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(this.refreshResults, 100)
    },
    onCompositionEnd() {
      this.composing = false
      this.scheduleSearch()
    },
    refreshResults() {
      clearTimeout(this.searchTimer)
      this.lastSearchedQuery = this.normalizedQuery
      if (this.status !== 'ready' || !this.normalizedQuery) {
        this.results = []
        this.overflow = false
      } else {
        const result = searchMenuIndex(this.indexEntries || [], this.normalizedQuery)
        this.results = result.results
        this.overflow = result.overflow
      }
      this.selectedIndex = 0
    },
    onKeydown(event) {
      if (this.composing || event.isComposing) return
      if (event.key === 'Escape') {
        this.panelVisible = false
        return
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (!this.results.length) return
        event.preventDefault()
        const delta = event.key === 'ArrowDown' ? 1 : -1
        this.selectedIndex = (this.selectedIndex + delta + this.results.length) % this.results.length
      } else if (event.key === 'Enter') {
        event.preventDefault()
        // 回车可能早于 100ms 防抖；必须先用当前输入同步刷新。
        if (this.lastSearchedQuery !== this.normalizedQuery) this.refreshResults()
        if (this.results.length) this.selectResult(this.results[this.selectedIndex])
      }
    },
    selectResult(item) {
      if (this.lastSearchedQuery !== this.normalizedQuery) this.refreshResults()
      // 菜单更新后，浮层里尚未刷新的旧项不允许跳转。
      if (this.status !== 'ready' || !this.results.some(entry => entry === item) ||
        !this.indexEntries.some(entry => entry.path === item.path && entry.title === item.title)) return
      const selectedQuery = this.normalizedQuery
      const complete = () => {
        this.history = rememberQuery(this.currentHistoryKey(), selectedQuery)
        this.panelVisible = false
        this.query = ''
        this.refreshResults()
      }
      if (isExternal(item.path)) {
        if (/^https?:/.test(item.path)) {
          const opened = window.open('', '_blank')
          if (opened) {
            opened.opener = null
            opened.location.href = item.path
            complete()
          }
        } else {
          const link = document.createElement('a')
          link.href = item.path
          link.target = '_blank'
          link.rel = 'noopener'
          link.click()
          // 浏览器不会返回 mailto/tel 处理结果，不能将其记为成功导航。
          this.panelVisible = false
          this.query = ''
          this.refreshResults()
        }
      } else if (this.$route.path === item.path) {
        complete()
      } else {
        this.$router.push(item.path, complete, () => {})
      }
    },
    useHistory(query) {
      this.query = query
      this.refreshResults()
      if (this.$refs.searchInput) this.$refs.searchInput.focus()
    },
    removeHistory(query) {
      this.history = deleteQuery(this.currentHistoryKey(), query)
    },
    removeAllHistory() {
      this.history = clearHistory(this.currentHistoryKey())
    },
    closeOnOutsideClick(event) {
      if (!this.panelVisible) return
      const root = this.$refs.searchRoot
      const panel = document.querySelector('.menu-search-popper')
      if (root && root.contains(event.target)) return
      if (panel && panel.contains(event.target)) return
      this.panelVisible = false
    }
  }
}
</script>

<style scoped>
.menu-search-reference { padding: 12px 14px 10px; background: #fff; }
.menu-search-collapsed { width: 100%; height: 34px; border: 0; background: #fff; color: #6b7686; font-size: 18px; cursor: pointer; }
.menu-search-collapsed:focus-visible, .menu-search-result:focus-visible, .menu-search-history button:focus-visible { outline: 2px solid #00bdb4; }
.menu-search-panel { max-height: 440px; overflow-y: auto; font-size: 13px; }
.menu-search-heading { display: flex; justify-content: space-between; color: #6b7686; font-size: 12px; padding: 7px 10px; }
.menu-search-empty, .menu-search-more { padding: 10px; margin: 0; color: #8b95a4; }
.menu-search-result { display: block; width: 100%; border: 0; border-radius: 4px; padding: 8px 10px; text-align: left; background: #fff; cursor: pointer; }
.menu-search-result:hover, .menu-search-result.is-selected, .menu-search-history:hover { background: #eefaf9; }
.menu-search-title { display: block; color: #2b3545; font-size: 13px; }
.menu-search-parents { display: block; color: #8b95a4; font-size: 12px; margin-top: 2px; }
.menu-search-history-tags { display: flex; flex-wrap: wrap; align-items: flex-start; padding: 4px 2px 2px 10px; }
.menu-search-history { display: inline-flex; align-items: center; min-width: 0; max-width: calc(100% - 8px); margin: 0 8px 8px 0; border-radius: 8px; background: #f5f7fa; }
.menu-search-history button, .menu-search-action { border: 0; background: transparent; cursor: pointer; }
.menu-search-history-query { min-width: 0; text-align: left; padding: 8px 4px 8px 12px; color: #435063; font-size: 13px; line-height: 20px; overflow-wrap: break-word; word-break: break-word; }
.menu-search-delete { flex-shrink: 0; min-width: 28px; min-height: 32px; padding: 6px 8px 6px 2px; font-size: 16px; line-height: 24px; color: #8b95a4; }
.menu-search-action { min-width: 28px; min-height: 28px; padding: 0 3px; color: #8b95a4; font-size: 16px; }
.menu-search-action:hover, .menu-search-delete:hover { color: #008f89; }
</style>

<style>
.menu-search-popper { box-sizing: border-box; max-width: calc(100vw - 24px); padding: 7px !important; }
</style>
